/* eslint-disable no-console */

import {
  EventSourceMessage,
  fetchEventSource,
} from "@microsoft/fetch-event-source";
import { useState, useCallback } from "react";
import { useSetAtom } from "jotai";
import { ZSL } from "@zapier/canvas-state";
import { isStreamingZslAtom } from "../atoms/streamingStatusAtoms";

interface TriggerRequestArgs {
  prompt?: string;
  currentZsl?: ZSL;
  tuningPrompts?: string[];
  apps?: string[];
  withIntegrations?: boolean;
}

type UseStreamingCompletionOptions = {
  api: string;
  /**
   * Whether to print a debug log to the console with the event stream messages.
   * @default true
   */
  debug?: boolean;
  /*
   * Rethrow the error to abort the event source operation. Otherwise, the
   * operation will automatically retry.
   */
  onTrigger?: () => void;
  onError?: (err: Error) => void;
  onMessage: (msg: EventSourceMessage) => void;
  onFinish?: () => void;
};

const streamLog: any[] = [];
const EventStreamTag = [
  `%cEventStream`,
  "color: white;background:#C5C5C5;font-weight: bold; font-size:8px; padding:2px 6px; border-radius: 5px",
];

// ## IF STREAMING DOESN"T WORK LOOK AT THE PROXY - ask Sholty first, then Ryan D, then Jake M
// This endpoint is exclusively to support localhost development
// In production it will be routed through cloudfront to the ZSL Creator API
// https://gitlab.com/zapier/zapier-terraform/-/blob/master/production/cloudfront.tf

export function useStreamingCompletion(options: UseStreamingCompletionOptions) {
  const [completion, setCompletion] = useState("");
  const setIsLoading = useSetAtom(isStreamingZslAtom);
  // Abort controller to cancel the current API call.
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  const debug = options?.debug ?? true;
  const debugName = `useStreamingCompletion(${options.api})`;

  const triggerRequest = useCallback(
    async (args: TriggerRequestArgs) => {
      options?.onTrigger?.();

      try {
        setIsLoading(true);

        const abortController = new AbortController();
        setAbortController(abortController);

        // Empty the completion immediately.
        setCompletion("");

        await fetchEventSource(options.api, {
          method: "POST",
          credentials: "include",
          headers: {
            "Cache-Control": "no-cache",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(args),
          signal: abortController.signal,
          // Keep the streaming going even if the tab is hidden.
          openWhenHidden: true,
          onmessage(msg) {
            if (debug) {
              streamLog.push(msg);
            }
            options.onMessage(msg);
          },
          onclose() {
            if (debug) {
              console.groupCollapsed(...EventStreamTag, debugName);
              console.table(streamLog);
              console.groupEnd();
            }
            options?.onFinish?.();
          },
          onerror(err) {
            if (debug) {
              console.groupCollapsed(...EventStreamTag, debugName);
              console.error(err);
              console.table(streamLog);
              console.groupEnd();
            }

            // Rethrowing stops the operation. Doing nothing will automatically retry.
            options?.onError?.(err);
          },
        });
      } finally {
        setIsLoading(false);
        setAbortController(null);
      }
    },
    [
      options,
      setIsLoading,
      setCompletion,
      setAbortController,
      debug,
      debugName,
    ],
  );

  const stop = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
  }, [abortController]);

  const handleSubmit = useCallback(
    (input: string, currentZsl?: ZSL, tuningPrompts?: string[]) => {
      return triggerRequest({
        prompt: input,
        currentZsl,
        tuningPrompts,
      });
    },
    [triggerRequest],
  );

  return {
    completion,
    stop,
    handleSubmit,
    triggerRequest,
  };
}
/* eslint-enable no-console */
